import { motion } from "framer-motion";
import { styled } from "styled-components";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { ButtonLink, ButtonRow, Container, Eyebrow, Lead, Pill } from "./UI";

const Wrap = styled.section`
  min-height: min(780px, calc(100vh - 82px));
  display:grid;
  align-items:center;
  padding: 4.5rem 0;
  overflow:hidden;
`;
const Layout = styled(Container)`
  display:grid;
  grid-template-columns:1.08fr .92fr;
  gap:clamp(2rem,6vw,5rem);
  align-items:center;
  @media(max-width:880px){grid-template-columns:1fr;}
`;
const H1 = styled.h1`
  font-size:clamp(3rem,7.4vw,6.6rem);
  line-height:.93;
  letter-spacing:-.062em;
  margin:0;
  max-width: 12ch;
  span { background:linear-gradient(100deg, ${({theme})=>theme.colors.primary}, ${({theme})=>theme.colors.accent}); color:transparent; background-clip:text; }
`;
const Visual = styled(motion.div)`
  min-height:500px;
  border-radius:38px;
  position:relative;
  overflow:hidden;
  border:1px solid ${({theme})=>theme.colors.border};
  background:
    radial-gradient(circle at 72% 18%, rgba(69,183,255,.28), transparent 35%),
    radial-gradient(circle at 25% 85%, rgba(21,200,117,.23), transparent 34%),
    ${({theme})=>theme.colors.surface};
  box-shadow:0 35px 100px ${({theme})=>theme.colors.shadow};
  display:grid;
  place-items:center;
`;
const LogoPlate = styled.div`
  position:relative;
  z-index:2;
  width:min(82%,500px);
  aspect-ratio:1;
  display:grid;
  place-items:center;
  border-radius:50%;
  background:rgba(255,255,255,.94);
  border:1px solid rgba(255,255,255,.85);
  box-shadow:0 24px 60px rgba(0,0,0,.2);
`;
const Logo = styled.img`
  width:94%;
  height:auto;
  filter:drop-shadow(0 16px 26px rgba(16,45,68,.18));
`;
const Orbit = styled.div`
  position:absolute;
  width:78%;
  aspect-ratio:1;
  border:1px solid ${({theme})=>theme.colors.border};
  border-radius:50%;
  animation:spin 24s linear infinite;
  &::before,&::after{content:"";position:absolute;width:15px;height:15px;border-radius:50%;background:${({theme})=>theme.colors.primary};box-shadow:0 0 28px ${({theme})=>theme.colors.primary};}
  &::before{top:5%;left:24%;}
  &::after{right:7%;bottom:24%;background:${({theme})=>theme.colors.accent};}
  @keyframes spin{to{transform:rotate(360deg)}}
`;
const Mini = styled.div<{ $left:string; $top:string }>`
  position:absolute;
  left:${p=>p.$left};
  top:${p=>p.$top};
  z-index:3;
  padding:.7rem .85rem;
  border-radius:14px;
  background:${({theme})=>theme.colors.surfaceElevated};
  border:1px solid ${({theme})=>theme.colors.border};
  box-shadow:0 14px 35px ${({theme})=>theme.colors.shadow};
  font-size:.8rem;
  font-weight:760;
  display:flex;
  align-items:center;
  gap:.4rem;
  @media(max-width:500px){display:none;}
`;

export function Hero() {
  return (
    <Wrap>
      <Layout>
        <motion.div initial={{opacity:0,y:22}} animate={{opacity:1,y:0}} transition={{duration:.65}}>
          <Eyebrow>Complete digital services for growing businesses</Eyebrow>
          <H1>Modern websites. <span>Smarter business.</span></H1>
          <Lead>TPeck Digital helps growing businesses build, modernize, and manage their digital presence—from website development, hosting, business email, SEO, and digital marketing to custom AI agents, workflow automation, and modern business tools.</Lead>
          <ButtonRow>
            <ButtonLink to="/contact#schedule">Schedule a Digital Solutions Consultation <ArrowRight size={18}/></ButtonLink>
            <ButtonLink to="/services" $secondary>Explore our services</ButtonLink>
          </ButtonRow>
          <div style={{display:"flex",flexWrap:"wrap",gap:".55rem",marginTop:"2rem"}}>
            <Pill>Websites</Pill><Pill>Hosting & email</Pill><Pill>SEO</Pill><Pill>Digital marketing</Pill><Pill>AI & automation</Pill>
          </div>
        </motion.div>
        <Visual initial={{opacity:0,scale:.96}} animate={{opacity:1,scale:1}} transition={{duration:.7,delay:.12}}>
          <Orbit/>
          <LogoPlate><Logo src="/tpeck-logo-transparent.png" alt="" width="1024" height="1024" /></LogoPlate>
          <Mini $left="5%" $top="10%"><CheckCircle2 size={15}/> Build</Mini>
          <Mini $left="75%" $top="15%"><CheckCircle2 size={15}/> Grow</Mini>
          <Mini $left="7%" $top="82%"><CheckCircle2 size={15}/> Automate</Mini>
          <Mini $left="70%" $top="78%"><CheckCircle2 size={15}/> Support</Mini>
        </Visual>
      </Layout>
    </Wrap>
  );
}
